import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useAuth } from '../context/AuthContext';

const routeByRole = {
  superadmin: '/admin',
  admin: '/admin',
  educator: '/educator',
  student: '/student',
  parent: '/parent',
};

const LoginPage = () => {
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { login } = useAuth();

  const onSubmit = async (values) => {
    try {
      setSubmitting(true);
      const data = await login(values);
      const redirectTo = routeByRole[data.user.role] || '/login';
      enqueueSnackbar(`Welcome back, ${data.user.name}!`, { variant: 'success' });
      const from = location.state?.from?.pathname;
      navigate(from || redirectTo, { replace: true });
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Login failed', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        py: 6,
      }}
    >
      <Container maxWidth="md">
        <Stack spacing={3} alignItems="center">
          <Box
            component="img"
            src="/logo2 copy.png"
            alt="Apex Homeschool logo"
            sx={{ width: 340, maxWidth: '95%', height: 'auto' }}
          />
          <Stack spacing={1} textAlign="center">
            <Typography variant="h3" sx={{ fontWeight: 800, color: 'primary.main' }}>
              Homeschool Platform
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Sign in to manage educators, students, and learning sessions.
            </Typography>
          </Stack>
          <Card
            sx={{
              width: '100%',
              maxWidth: 640,
              mx: 'auto',
              borderRadius: 3,
              boxShadow: 3,
            }}
          >
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <Stack spacing={3}>
                  <TextField
                    label="Email address"
                    type="email"
                    fullWidth
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /\S+@\S+\.\S+/,
                        message: 'Enter a valid email address',
                      },
                    })}
                  />
                  <TextField
                    label="Password"
                    type="password"
                    fullWidth
                    error={Boolean(errors.password)}
                    helperText={errors.password?.message}
                    {...register('password', {
                      required: 'Password is required',
                    })}
                  />
                  <FormControlLabel
                    control={<Checkbox color="primary" {...register('rememberMe')} />}
                    label="Remember me on this device"
                  />
                  <Button
                    type="submit"
                    size="large"
                    variant="contained"
                    disabled={submitting}
                  >
 {submitting ? 'Signing in...' : 'Sign In'}
                  </Button>
                </Stack>
              </form>
            </CardContent>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
};

export default LoginPage;